import { AssetWorkflowStep } from './AssetWorkflowStep';
import type {
  Asset,
  AssetActionPayload,
  AssetState,
  AssetTypes,
  AssetWorkflowOptions,
  AssetWorkflowStepOptions,
  WorkflowComponentType,
  WorkflowContext,
  WorkflowSingleSelectProps,
} from './types';
import { Workflow } from './Workflow';
type StepEntry = { index: number; value: string };

export class AssetWorkflow extends Workflow {
  public readonly assetType: AssetTypes;
  public readonly workflowOptions: AssetWorkflowOptions;
  public workflowStepMap: Map<string, AssetWorkflowStep>;
  public workflowSteps: AssetWorkflowStep[];
  public assetId?: string;
  public readonly deleteAction: boolean;

  constructor(
    workflowGroupKey: string,
    options: AssetWorkflowOptions,
    assetState: AssetState,
    isShipiumAdmin?: boolean,
  ) {
    super(workflowGroupKey, options, assetState, isShipiumAdmin);
    this.assetType = options.assetType;
    this.workflowOptions = options;
    this.deleteAction = options.deleteAction === true ? true : false;
    this.workflowSteps = AssetWorkflow.constructSteps(
      this.workflowKey,
      options.workflowStepOptions,
      assetState,
      isShipiumAdmin,
    );
    this.workflowStepMap = AssetWorkflow.constructStepMap(this.workflowSteps);
  }

  public static constructStepMap(workflowSteps: AssetWorkflowStep[]): Map<string, AssetWorkflowStep> {
    return workflowSteps.reduce((prev, curr) => {
      return prev.set(curr.workflowStepKey, curr);
    }, new Map<string, AssetWorkflowStep>());
  }

  public static constructSteps(
    workflowKey: string,
    workflowStepOptions: AssetWorkflowStepOptions[],
    assetState: AssetState,
    isShipiumAdmin?: boolean,
    asset?: Asset,
  ): AssetWorkflowStep[] {
    return workflowStepOptions
      .filter((option) => isShipiumAdmin || !option.shipiumOnly)
      .map((option, index) => {
        option.workflowStepIndex = option.workflowStepIndex || index + 1;
        return option;
      })
      .sort((a, b) =>
        (a.workflowStepIndex || 0) < (b.workflowStepIndex || 0)
          ? -1
          : (a.workflowStepIndex || 0) > (b.workflowStepIndex || 0)
            ? 1
            : 0,
      )
      .map((options) => new AssetWorkflowStep(workflowKey, options, assetState));
  }

  public static getLinkPropertiesForAssetType(assetType: AssetTypes) {
    type AssetMappings = { [asset in AssetTypes]?: Record<string, AssetTypes> };
    const mappings: AssetMappings = {
      origin: {
        addressId: 'address',
        contactId: 'contact',
        tagIds: 'tags',
      },
      carrierAccount: {
        shipperAddressId: 'address',
        shipperContactId: 'contact',
        returnToAddressId: 'address',
        returnToContactId: 'contact',
        sortFacilityAddressId: 'address',
        sortFacilityContactId: 'contact',
        tagIds: 'tags',
      },
      contract: {
        carrierSettingsId: 'carrierSettings',
        carrierAccountId: 'carrierAccount',
        carrierRateSetId: 'carrierRateSet',
        tagIds: 'tags',
      },
      carrierRateSet: {
        carrierRateIds: 'carrierRate',
        tagIds: 'tags',
      },
      fulfillmentContextLinkProperty: {
        carrierAccountId: 'carrierAccount',
        origin: 'origin',
      },
      fulfillmentContext: {
        labelPropertiesId: 'labelProperties',
        returnsSettingsId: 'returnsSettings',
        scheduleId: 'schedule',
        contractIds: 'contract',
        packageSetId: 'packaging',
        tagIds: 'tags',
        augmentationSetId: 'augmentationSet',
      },
    };
    return mappings[assetType];
  }

  public static getAssetIdKeyForOverride(linkAssetTypeOverride: string) {
    switch (linkAssetTypeOverride) {
      case 'sortFacilityAddress':
        return 'sortFacilityAddressId';
      case 'shipperAddresses':
        return 'shipperAddressId';
      case 'returnToAddress':
        return 'returnToAddressId';
      case 'returnToContact':
        return 'returnToContactId';
      case 'usppiAddress':
        return 'unitedStatesPrincipalPartyInInterestAddressId';
      case 'usppiContact':
        return 'unitedStatesPrincipalPartyInInterestContactId';
      case 'sortFacilityContact':
        return 'sortFacilityContactId';
      case 'shipperContact':
        return 'shipperContactId';
      case 'shipperContacts':
        return 'shipperContactId';
      case 'returnToContactInfo':
        return 'returnToContactInfoId';
      case 'usppiAddresses':
        return 'unitedStatesPrincipalPartyInInterestAddressId';
      case 'usppiContacts':
        return 'unitedStatesPrincipalPartyInInterestContactId';
      case 'thirdPartyBillingAccountSet':
        return 'thirdPartyBillingAccountSetId';
      case 'packagings':
        return 'packagingId';
      default:
        throw new Error('Not mapped override type');
    }
  }
  public static getAssetIdKeyForAssetType(assetType: AssetTypes) {
    switch (assetType) {
      case 'labelAugmentation':
        return 'labelAugmentationId';
      case 'contract':
        return 'contractId';
      case 'origin':
        return 'originId';
      case 'address':
        return 'addressId';
      case 'contact':
        return 'contactId';
      case 'carrierAccount':
        return 'carrierAccountId';
      case 'carrierRate':
        return 'carrierRateId';
      case 'schedule':
        return 'scheduleId';
      case 'carrierSettings':
        return 'carrierSettingsId';
      case 'fulfillmentContext':
        return 'fulfillmentContextId';
      case 'labelProperties':
        return 'labelPropertiesId';
      case 'fulfillmentContextLinkProperty':
        return 'fulfillmentContextLinkPropertyId';
      case 'carrier':
        return 'carrierId';
      case 'carrierRateSet':
        return 'carrierRateSetId';
      case 'tags':
        return 'tagId';
      case 'carrierServiceMethod':
        return 'carrierServiceMethodId';
      case 'augmentationSet':
        return 'augmentationSetId';
      case 'packaging':
        return 'packagingId';
      case 'packagingSet':
        return 'packagingSetId';
      case 'returnsSettings':
        return 'returnsSettingsId';
      case 'tenant':
        return 'tenantId';
      case 'thirdPartyBillingAccount':
        return 'thirdPartyBillingAccountId';
      case 'thirdPartyBillingAccountSet':
        return 'thirdPartyBillingAccountSetId';
      case 'returnAddressOverride':
        return 'returnToAddressOverrideId';
      case 'laneSortConfigurations':
        return 'laneSortConfigurationId';
      default:
        throw new Error(`Unknown asset type ${assetType}`);
    }
  }

  public static getAssetTypeForSelectWorkflowComponentType(
    componentType: WorkflowComponentType,
  ): AssetTypes | undefined {
    switch (componentType) {
      case 'carrierAccountSelect':
        return 'carrierAccount';
      case 'contactEntry':
        return 'contact';
      case 'addressEntry':
        return 'address';
      case 'carrierSettingSelect':
        return 'carrierSettings';
      case 'scheduleSelect':
        return 'schedule';
    }
  }

  public static getWorkFlowGroupKeyForAssetType(assetType: AssetTypes) {
    switch (assetType) {
      case 'origin':
        return 'fulfillmentCenters';
      case 'address':
        return 'addresses';
      case 'fulfillmentContext':
        return 'carrierSelection';
      case 'fulfillmentContextLinkProperty':
        return 'carrierSelectionOverrides';
      case 'carrierSettings':
        return 'carrierSettings';
      case 'carrierAccount':
        return 'carrierAccounts';
      case 'carrierRate':
        return 'carrierRates';
      case 'labelProperties':
        return 'labelProperties';
      case 'labelAugmentation':
        return 'labelAugmentations';
      case 'schedule':
        return 'schedules';
      case 'contact':
        return 'contacts';
      case 'contract':
        return 'contracts';
      case 'tags':
        return 'tags';
      case 'thirdPartyBillingAccount':
        return 'thirdPartyBillingAccount';
      default:
        return assetType;
    }
  }

  public static getDisplayNameForAssetType(assetType: AssetTypes) {
    switch (assetType) {
      case 'origin':
        return 'Origins';
      case 'address':
        return 'Address';
      case 'augmentationSet':
        return 'Augmentation Set';
      case 'contact':
        return 'Contacts';
      case 'contract':
        return 'Contracts';
      case 'carrierAccount':
        return 'Carrier Accounts';
      case 'carrierRate':
        return 'Carrier Rates';
      case 'carrierRateSet':
        return 'Carrier Rate Sets';
      case 'schedule':
        return 'Schedule';
      case 'carrierSettings':
        return 'Carrier Settings';
      case 'fulfillmentContext':
        return 'Fulfillment Contexts';
      case 'labelAugmentation':
        return 'Label Augmentations';
      case 'labelProperties':
        return 'Label Properties';
      case 'fulfillmentContextLinkProperty':
        return 'Overrides';
      case 'packaging':
        return 'Packaging';
      case 'packagingSet':
        return 'Packaging Sets';
      case 'returnsSettings':
        return 'Returns';
      case 'tags':
        return 'Tags';
      case 'carrier':
        return 'Carriers';
      case 'carrierServiceMethod':
        return 'Carrier Service Methods';
      case 'tenant':
        return 'Tenant';
      case 'thirdPartyBillingAccount':
        return 'Third Party Billing Account';
      case 'thirdPartyBillingAccountSet':
        return 'Third Party Billing Group';
      case 'returnAddressOverride':
        return 'Return to Address Overrides';
      case 'laneSortConfigurations':
        return 'Carrier Shipment Lane Configuration';
      default:
        throw new Error(`Unknown asset type ${assetType}`);
    }
  }

  public get displayName(): string {
    return AssetWorkflow.getDisplayNameForAssetType(this.assetType);
  }

  public buildAssetNameInitialValue(workflowContext: WorkflowContext) {
    const { workflowState, workflowAssets } = workflowContext;
    const workflowStatus = this.status(workflowContext);

    const initialValueStepKeys = workflowStatus.workflowStepStatus.reduce<string[]>((prev, step) => {
      const workflowStep = this.workflowStepMap.get(step.workflowStepKey);
      const isSummary = workflowStep?.isSummaryStep() || false;
      const includeInDefaultAssetName =
        workflowStep?.includeInDefaultAssetNameRules?.includeInDefaultAssetName || false;
      if (includeInDefaultAssetName && step?.workflowStepValid && !isSummary) {
        prev.push(step.workflowStepKey);
      }
      return prev;
    }, []);

    const stepEntries = initialValueStepKeys.reduce<StepEntry[]>((prev, curr, currentIndex) => {
      const workflowStep = this.workflowStepMap.get(curr);
      const workflowStepComponent = workflowStep?.workflowComponent;
      const { prefix = '', suffix = '', index = currentIndex } = workflowStep?.includeInDefaultAssetNameRules || {};
      const formatStepEntry = (v: string) => {
        return { index, value: `${prefix}${v}${suffix}` };
      };
      if (!workflowStep || !workflowStepComponent) {
        return prev;
      }
      const value = workflowState[this.workflowKey]?.[workflowStep.workflowStepKey];
      const mappingAssetType = AssetWorkflow.getAssetTypeForSelectWorkflowComponentType(
        workflowStepComponent.workflowComponentType,
      );

      // This should handle any links that are asset shaped.  Be sure to add new select components in getAssetTypeForSelectWorkflowComponentType.
      if (mappingAssetType) {
        const assets = workflowAssets[mappingAssetType] || [];
        const asset = assets.find((c) => c.carrierAccountId === value);
        if (asset) {
          prev.push(formatStepEntry(asset.name));
        }
      }

      if (workflowStepComponent.workflowComponentType === 'singleSelect') {
        const singleSelectComponent = workflowStepComponent as WorkflowSingleSelectProps;
        const wfcValue = singleSelectComponent.options.find((option) => option.optionValue === value);
        if (wfcValue) {
          prev.push(formatStepEntry(wfcValue.optionText));
        }
      }

      if (workflowStepComponent.workflowComponentType === 'textEntry') {
        if (value) {
          prev.push(formatStepEntry(value));
        }
      }

      if (workflowStepComponent.workflowComponentType === 'carrierEntry') {
        const carrier = workflowAssets.carrier.find((c) => c.carrierId === value);
        if (carrier) {
          prev.push(formatStepEntry(carrier.name!));
        }
      }

      if (workflowStepComponent.workflowComponentType === 'carrierServiceMethodEntry') {
        const carrierServiceMethod = workflowAssets.carrierServiceMethod.find((c) => c.serviceMethodId === value);
        if (carrierServiceMethod) {
          prev.push(formatStepEntry(carrierServiceMethod.name));
        }
        const carrier = workflowAssets.carrier.find((c) => c.carrierId === value);
        if (carrier) {
          prev.push(formatStepEntry(carrier.name!));
        }
      }

      return prev;
    }, []);

    stepEntries.sort((a, b) => a.index - b.index);

    return stepEntries.map((entry) => entry.value).join(' ');
  }

  public buildAssetActionPayload(workflowContext: WorkflowContext) {
    const assetActionPayload: AssetActionPayload = {
      // fulfillmentContextLinkProperties uses a delete instead of a put.  This extra bit of logic is needed to support this edge case.
      assetActionType: this.workflowType === 'addAssetWorkflow' ? 'add' : this.deleteAction ? 'delete' : 'update',
      assetType: this.assetType,
      assetId: this.assetId,
      assetLinks: [],
      overrideSave: this.workflowOptions.overrideSave,
    };
    for (const step of this.workflowSteps) {
      let stepValue = workflowContext.workflowState[this.workflowKey][step.workflowStepKey];
      if (step?.workflowStateToAssetMapping?.workflowStateValueOverride) {
        stepValue = step.workflowStateToAssetMapping.workflowStateValueOverride(
          workflowContext.workflowState[this.workflowKey],
        );
      }
      if (step.workflowStateToAssetMapping?.mappingType === 'property') {
        if (!assetActionPayload.assetPayload) {
          assetActionPayload.assetPayload = {};
        }
        assetActionPayload.assetPayload[step.workflowStateToAssetMapping.property] = stepValue;
      } else if (step.workflowStateToAssetMapping?.mappingType === 'properties') {
        if (!assetActionPayload.assetPayload) {
          assetActionPayload.assetPayload = {};
        }
        step.workflowStateToAssetMapping.properties.forEach((p) => {
          (assetActionPayload.assetPayload as Record<string, any>)[p] = stepValue;
        });
      } else if (step.workflowStateToAssetMapping?.mappingType === 'nestedProperty') {
        const parent = step.workflowStateToAssetMapping.parent;
        assetActionPayload.assetPayload = assetActionPayload.assetPayload || {};
        assetActionPayload.assetPayload[parent] = assetActionPayload.assetPayload[parent] || {};
        assetActionPayload.assetPayload[parent][step.workflowStateToAssetMapping.property] = stepValue;
      } else if (step.workflowStateToAssetMapping?.mappingType === 'link') {
        if (Array.isArray(stepValue)) {
          // if this is an edit step, let the buildAssetActionPayload method on editAssetWorkflow handle linking and unlinking
          const editAsset = (step as any).asset;
          if (!editAsset) {
            for (const value of stepValue) {
              assetActionPayload.assetLinks.push({
                assetType: step.workflowStateToAssetMapping.linkAssetType,
                assetId: value,
                linkAssetTypeOverride: step.workflowStateToAssetMapping.linkAssetTypeOverride,
                linkType: step.workflowStateToAssetMapping.linkType,
              });
            }
          }
        } else {
          if (stepValue) {
            assetActionPayload.assetLinks.push({
              assetType: step.workflowStateToAssetMapping.linkAssetType,
              assetId: stepValue,
              linkAssetTypeOverride: step.workflowStateToAssetMapping.linkAssetTypeOverride,
              linkType: step.workflowStateToAssetMapping.linkType,
            });
          }
        }
      }
    }
    return assetActionPayload;
  }
}
